import React, {useState, useEffect, useRef} from 'react';

import Icon from '../Icon';
import Checkbox from '../Checkbox';

import {SelectProps} from './interface';
import {ChildrenElement} from '../../typescript-declaration/global';

import * as ft_divPosition from '../../functions/divPosition';
import ft_cssVar from '../../functions/getCssVar';

import img_chevronDown from '../../images/icon/chevron-down.svg';
import img_check from '../../images/icon/check.svg';
import img_close from '../../images/icon/close.svg';

import './Select.css';


const defaultProps: SelectProps = {
	data:          [],
	valueName:     '',
	value:         '',
	placeholder:   '',
	onChange:      () => undefined,
	title:         '',
	required:      false,
	error:         false,
	requiredError: false,
	style:         {},
	styleAbsolute: {},
	readOnly:      false
};

function Select(props: SelectProps): JSX.Element {
	props = {...defaultProps, ...props};

	const [open, set_open] = useState(false);
	const [hover, set_hover] = useState(false);
	const [anim, set_anim] = useState(false);
	const [saveSize, set_saveSize] = useState<number | undefined>();
	const [absolutePosition, set_absolutePosition] = useState({top: 0, left: 0, minWidth: 0, maxWidth: 0});

	const selectEl = useRef<HTMLDivElement>(null);

	useEffect(() => {
		function handleScroll() {
			if (open && selectEl.current) {
				const position = ft_divPosition.getWindowPosition(selectEl.current);

				let maxWidth = (selectEl?.current?.offsetWidth - 2) * 4;

				if (maxWidth + position.left > window.innerWidth - 10)
					maxWidth = window.innerWidth - 10 - position.left;

				set_absolutePosition({
					maxWidth,
					left:     position.left,
					minWidth: selectEl.current.offsetWidth - 2,
					top:      (position.top + selectEl?.current?.offsetHeight) + 3
				});
			}
		}

		if (!open)
			set_absolutePosition({top: 0, left: 0, minWidth: 0, maxWidth: 0});
		else if (!anim)
			set_anim(true);

		handleScroll();

		const parentElements: HTMLElement[] = [];
		let 	currentElement: HTMLElement | null = selectEl.current;

		while (currentElement) {
			const parent = currentElement.parentElement;
			if (parent) {
				parentElements.push(parent);
				currentElement = parent;
			} else
				currentElement = null;
		}

		parentElements.forEach(element => element.addEventListener('scroll', handleScroll));

		return () => parentElements.forEach(element => element.removeEventListener('scroll', handleScroll));
	}, [open]);

	useEffect(() => {
		if (!saveSize)
			set_saveSize(selectEl.current?.offsetWidth);
	}, [selectEl]);

	const foundData = props.data?.find(find => props.value && find?.value === props.value);
	const render: React.ReactElement[] = [];
	let	name: ChildrenElement = props.readOnly ? '' : 'Selectionner...';
	let isPlaceholder = true;
	let className = '';

	function onClick() {
		if (props.readOnly)
			return;
		if (props.onClick)
			return props.onClick();
		set_open(!open);
	}

	if (open && props.data) {
		props.data.forEach((e, i) => {
			if (!e)
				return;

			const onClickItem = () => {
				if (props.onClickCheckbox)
					return props.onClickCheckbox(e.value);
				if (props.onChange && foundData?.value !== e.value)
					props.onChange(e.value);
				set_open(false);
			};

			let active = false;

			if (props.checkboxChecked && props.checkboxChecked(e.value))
				active = true;
			else if (Array.isArray(props.value) && props.value?.some(find => find === e.value))
				active = true;

			render.push(
				<div
					key={`select-${i}`}
					className={`selectItem ${e.disabled && 'disabled'}`}
					onClick={() => (!e.disabled) && onClickItem()}
				>
					{(!!props.checkboxChecked || Array.isArray(props.value)) &&
						<Checkbox
							active={active}
							style={{marginRight: 5}}
						/>
					}
					<div className={'flex'}>{e.render}</div>
					{foundData?.value === e.value && <Icon icon={img_check} />}
				</div>
			);
		});
	}

	if (props.value && foundData?.value) {
		name = foundData.render;
		isPlaceholder = false;
	} else if (Array.isArray(props.value) && props.value?.length) {
		if (props.arrayUseRender) {
			const nameRender: ChildrenElement[] = [];

			props.value.forEach((f) => {
				const find = props.data?.find(f2 => f2?.value === f);

				if (find)
					nameRender.push(find.render);
			});

			name = <div style={{gap: 16}}>{nameRender}</div>;
		} else
			name = props.value?.join(', ');
		isPlaceholder = false;
	} else if (props.placeholder)
		name = props.placeholder;

	let	error = props.error;

	if (props.required && !props.value && props.requiredError)
		error = true;

	if (props.type)
		className += `${props.type}-50-background ${props.type}-50-border`;

	return (
		<div className={`selectMainContainer ${!props.notFullWidth && 'fullWidth'}`} >
			{props.title &&
				<span className={'selectTitle'}>
					{props.title}{props.required && '*'}
				</span>
			}
			<div
				tabIndex={0}
				ref={selectEl}
				role={'button'}
				onClick={onClick}
				style={{...props.style, width: (props.notFullWidth && saveSize) ? saveSize : props.style?.width}}
				onMouseEnter={() => set_hover(true)}
				onMouseLeave={() => set_hover(false)}
				onBlur={() => !hover && set_open(false)}
				className={`selectContainer ${error && 'error'} ${props.readOnly && 'readOnly'}  ${props.readOnlySp && 'sp'} ${className}`}
			>
				{props.icon &&
					<div className={'selectIcon'}>
						<Icon
							icon={props.icon}
							iconColor={props.type ? ft_cssVar(`${props.type}-700`) : undefined}
						/>
					</div>
				}
				<div className={`selectText text-${props.size || 'sm'} weight-400 gray-900 ${isPlaceholder && 'placeholder'} ${isPlaceholder && props.placeholderClassname} ${props.type && `${props.type}-700`} ${props.classNameValue} ${props.size}`} >
					<span>{name || <span>&nbsp;</span>}</span>
				</div>

				{props.reset &&
					<div
						className={'selectChevron'}
						onClick={(e) => {
							e.stopPropagation();
							if (props.onReset)
								props.onReset();
						}}
					>
						<Icon
							iconSize={10}
							icon={img_close}
							iconColor={ft_cssVar('error-700')}
						/>
					</div>
				}

				{(!props.readOnly && !props.hideChevron) &&
					<div className={`selectChevron ${open ? 'open' : 'close'} ${anim && 'anim'}`}>
						<Icon
							iconSize={10}
							icon={img_chevronDown}
							iconColor={props.type ? ft_cssVar(`${props.type}-700`) : undefined}
						/>
					</div>
				}

				{(open && absolutePosition.top !== 0) &&
					<div
						className={'selectAbsolute'}
						onClick={e => e.stopPropagation()}
						style={{
							...props.styleAbsolute,
							...absolutePosition
						}}
					>
						<div className={'selectAbsoluteContent'} style={props.styleAbsolute}>
							{render}
						</div>
					</div>
				}

			</div>

			{(error && typeof(error) === 'string') &&
				<span className={'inputText error'}>
					{props.error}
				</span>
			}
		</div>
	);
}

export default Select;